import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, LineChart, Layers, Compass, Database, Gauge, FileText, Scale } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const iconsWhy = [Database, Gauge, Layers, LineChart];
const iconsTransparency = [ShieldCheck, Scale, FileText, Compass];

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});

  const whyItems = t("why.items", []);
  const sectorItems = t("sectors.items", []);
  const processSteps = t("process.steps", []);
  const insightCards = t("insights.cards", []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = true;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = true;
    if (!form.consent) next.consent = true;
    setErrors(next);
    if (Object.keys(next).length === 0) {
      navigate("/thank_you");
    }
  };

  return (
    <main className="bg-white" data-testid="home-page">
      {/* HERO */}
      <section id="overview" className="relative overflow-hidden bg-[#0B2447] text-white">
        <div className="absolute inset-0">
          <img
            src="/assets/hero-salinas.jpg"
            alt=""
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-[#0B2447]/70" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.24em] text-[#E0A78E]" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </p>
            <h1 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-4 font-serif text-xl italic text-[#E0A78E] sm:text-2xl">
              {t("hero.subtitle")}
            </p>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
              {t("hero.lead")}
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
              {t("hero.lead_2")}
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#C56E4E] px-7 py-3 text-sm uppercase tracking-[0.14em] text-white hover:bg-[#a95940]"
              >
                {t("hero.cta_primary")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <a
                href="#sectors"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-slate-300/60 px-7 py-3 text-sm uppercase tracking-[0.14em] text-white hover:border-white"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[#2a4a75] bg-[#0f2c58]/80 p-6 backdrop-blur">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-300">
                Argentina · Q3 2026
              </p>
              <dl className="mt-6 divide-y divide-[#1e3d68]">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex items-baseline justify-between py-4">
                    <dt className="pr-6 text-sm text-slate-300">
                      {t(`hero.stat_${n}_label`)}
                    </dt>
                    <dd className="font-serif text-2xl text-white sm:text-3xl">
                      {t(`hero.stat_${n}_value`)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="border-b border-slate-200 bg-[#F5F3EF]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl text-[#0B2447] sm:text-4xl lg:text-5xl" data-testid="why-title">
              {t("why.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("why.intro")}
            </p>
          </div>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(whyItems) &&
              whyItems.map((item, i) => {
                const Icon = iconsWhy[i % iconsWhy.length];
                return (
                  <article
                    key={i}
                    data-testid={`why-item-${i}`}
                    className="border-t border-[#0B2447] pt-6"
                  >
                    <Icon className="h-6 w-6 text-[#C56E4E]" aria-hidden />
                    <h3 className="mt-4 font-serif text-xl text-[#0B2447]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                      {item.text}
                    </p>
                  </article>
                );
              })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C56E4E]">
              Argentina · 2026
            </p>
            <h2 className="mt-4 font-serif text-3xl text-[#0B2447] sm:text-4xl lg:text-5xl" data-testid="sectors-title">
              {t("sectors.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("sectors.intro")}
            </p>
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {[
              { img: "/assets/sector-lithium.jpg" },
              { img: "/assets/sector-oil.jpg" },
              { img: "/assets/sector-mine.jpg" },
              { img: "/assets/sector-infra.jpg" }
            ].map((cfg, i) => {
              const item = Array.isArray(sectorItems) ? sectorItems[i] : null;
              if (!item) return null;
              return (
                <article
                  key={i}
                  data-testid={`sector-card-${i}`}
                  className="grid grid-cols-1 border border-slate-200 sm:grid-cols-5"
                >
                  <div className="sm:col-span-2">
                    <img
                      src={cfg.img}
                      alt={item.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="sm:col-span-3 p-7">
                    <span className="inline-block border border-[#0B2447] px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-[#0B2447]">
                      {item.tag}
                    </span>
                    <h3 className="mt-4 font-serif text-2xl text-[#0B2447]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                      {item.text}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="border-y border-slate-200 bg-[#0B2447] text-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl" data-testid="process-title">
              {t("process.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-200 sm:text-lg">
              {t("process.intro")}
            </p>
          </div>
          <ol className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(processSteps) &&
              processSteps.map((step, i) => (
                <li
                  key={i}
                  data-testid={`process-step-${i}`}
                  className="border-t border-[#C56E4E] pt-6"
                >
                  <span className="font-serif text-5xl text-[#C56E4E]">
                    {step.num}
                  </span>
                  <h3 className="mt-4 font-serif text-xl">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">
                    {step.text}
                  </p>
                </li>
              ))}
          </ol>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:px-8">
          <div className="lg:col-span-5">
            <img
              src="/assets/dashboard-analytics.jpg"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C56E4E]">
              Xorvyniq
            </p>
            <h2 className="mt-4 font-serif text-3xl text-[#0B2447] sm:text-4xl lg:text-5xl" data-testid="transparency-title">
              {t("transparency.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("transparency.p1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("transparency.p2")}
            </p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((n) => {
                const Icon = iconsTransparency[n - 1];
                return (
                  <li
                    key={n}
                    className="flex gap-3 border-l-2 border-[#C56E4E] pl-4"
                    data-testid={`transparency-point-${n}`}
                  >
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#0B2447]" aria-hidden />
                    <span className="text-sm text-slate-800">
                      {t(`transparency.point_${n}`)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section id="insights" className="bg-[#F5F3EF]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl text-[#0B2447] sm:text-4xl lg:text-5xl" data-testid="insights-title">
              {t("insights.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("insights.intro")}
            </p>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(insightCards) &&
              insightCards.map((card, i) => (
                <div
                  key={i}
                  data-testid={`insight-card-${i}`}
                  className="border-t-2 border-[#0B2447] bg-white p-6"
                >
                  <p className="font-serif text-3xl text-[#0B2447] sm:text-4xl">
                    {card.kpi}
                  </p>
                  <p className="mt-3 text-sm font-medium text-slate-800">
                    {card.label}
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.14em] text-slate-500">
                    {card.source}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:px-8">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C56E4E]">
              {t("newsletter.eyebrow")}
            </p>
            <h2 className="mt-4 font-serif text-3xl text-[#0B2447] sm:text-4xl lg:text-5xl" data-testid="newsletter-title">
              {t("newsletter.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("newsletter.text")}
            </p>
          </div>
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="newsletter-form"
              className="border border-slate-200 bg-white p-6 sm:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="nl-name"
                    className="block text-xs uppercase tracking-[0.14em] text-[#0B2447]"
                  >
                    {t("newsletter.name_label")}
                  </label>
                  <input
                    id="nl-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t("newsletter.name_placeholder")}
                    data-testid="newsletter-name-input"
                    className={`mt-2 block w-full border bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-[#0B2447] ${
                      errors.name ? "border-[#C56E4E]" : "border-slate-300"
                    }`}
                  />
                </div>
                <div>
                  <label
                    htmlFor="nl-email"
                    className="block text-xs uppercase tracking-[0.14em] text-[#0B2447]"
                  >
                    {t("newsletter.email_label")}
                  </label>
                  <input
                    id="nl-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder={t("newsletter.email_placeholder")}
                    data-testid="newsletter-email-input"
                    className={`mt-2 block w-full border bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-[#0B2447] ${
                      errors.email ? "border-[#C56E4E]" : "border-slate-300"
                    }`}
                  />
                </div>
              </div>
              <label className="mt-5 flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  data-testid="newsletter-consent-checkbox"
                  className="mt-1 h-4 w-4 border-slate-400 accent-[#0B2447]"
                />
                <span className={errors.consent ? "text-[#C56E4E]" : ""}>
                  {t("newsletter.consent")}
                </span>
              </label>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">{t("newsletter.note")}</p>
                <button
                  type="submit"
                  data-testid="newsletter-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#0B2447] px-7 py-3 text-sm uppercase tracking-[0.14em] text-white hover:bg-[#0e2c58]"
                >
                  {t("newsletter.submit")}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
